// Whiteboard — the Whiteboard field on a work item. Loads the board, opens
// the live session (saving + everyone else on it), and shows the board in
// the work item — or over the whole screen (the Full screen button; Esc or
// "Exit full screen" brings it back, where it was). In the work item the
// board fits the panel it is in (no scrolling to reach its tools) unless the
// person dragged it to a height of their own (double-click the grip: fit again).

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import whiteboardApi from '@modules/database/whiteboard/frontend/api';
import { BoardModel } from '@modules/database/whiteboard/frontend/engine/model';
import { FileCache } from '@modules/database/whiteboard/frontend/files';
import { createSession } from '@modules/database/whiteboard/frontend/sync';
import Board from '@modules/database/whiteboard/frontend/Board';
import { Icon } from '@modules/database/whiteboard/frontend/ui';
import '@modules/database/whiteboard/frontend/Whiteboard.css';

// a height the person dragged the board to (none: the board fits its panel)
const HEIGHT_KEY = 'stride.whiteboard.boardHeight';
const readHeight = () => { try { return Number(localStorage.getItem(HEIGHT_KEY)) || null; } catch { return null; } };
const FIT_MIN = 420; const FIT_MAX = 1100;
const FOOT = 44;   // the "Updated … by …" line under the board
const errText = (e) => e?.response?.data?.error || e?.message || 'Something went wrong';
function when(at) {
  if (!at) return '';
  const d = new Date(at);
  return d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function WhiteboardField({ issueKey, fieldId, fieldName, onAttachmentAdded }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [full, setFull] = useState(false);
  const [manual, setManual] = useState(readHeight);
  const [fit, setFit] = useState(560);
  const height = manual || fit;
  const hostRef = useRef(null);
  // Fit the board to what is in view: from its top to the bottom of the panel
  // that scrolls it (a work item pop-up) or of the window.
  useLayoutEffect(() => {
    if (manual) return undefined;
    const measure = () => {
      const el = hostRef.current; if (!el) return;
      const top = el.getBoundingClientRect().top;
      let bottom = window.innerHeight;
      for (let p = el.parentElement; p; p = p.parentElement) {
        const oy = getComputedStyle(p).overflowY;
        if (oy === 'auto' || oy === 'scroll') { bottom = Math.min(bottom, p.getBoundingClientRect().bottom); break; }
      }
      const h = Math.round(Math.max(FIT_MIN, Math.min(FIT_MAX, bottom - top - FOOT)));
      setFit((f) => (Math.abs(f - h) > 8 ? h : f));
    };
    measure();
    const t = setTimeout(measure, 350);   // a pop-up may still be opening
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [manual, !!data]);
  const [session, setSession] = useState(null);
  const modelRef = useRef(null);
  const filesRef = useRef(null);
  const memory = useRef({});      // the view and tool, kept across inline ↔ full screen
  const fullRef = useRef(null);
  if (!modelRef.current) modelRef.current = new BoardModel();
  if (!filesRef.current) filesRef.current = new FileCache();

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await whiteboardApi.board(issueKey, fieldId);
      modelRef.current.load(d.elements || []);
      for (const el of d.elements || []) if (el.type === 'image') filesRef.current.ensure(el.fileId);
      setData(d);
    } catch (e) { setError(errText(e)); }
  }, [issueKey, fieldId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return undefined;
    const s = createSession({ model: modelRef.current, files: filesRef.current, key: issueKey, fieldId, room: data.room, canEdit: !!data.canEdit });
    setSession(s);
    return () => { s.stop(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.room, data?.canEdit]);
  useEffect(() => () => filesRef.current?.dispose(), []);

  // Full screen: the board over the whole window — and the whole screen where
  // the browser allows it. Leaving the browser's full screen (Esc) leaves ours.
  useEffect(() => {
    if (!full) return undefined;
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    let entered = false;
    const onChange = () => {
      if (document.fullscreenElement === fullRef.current) entered = true;
      else if (entered && !document.fullscreenElement) setFull(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    try { fullRef.current?.requestFullscreen?.({ navigationUI: 'hide' })?.catch?.(() => {}); } catch { /* the window overlay is enough */ }
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      html.style.overflow = prevOverflow;
      if (document.fullscreenElement) document.exitFullscreen?.().catch?.(() => {});
    };
  }, [full]);

  // a dragged height sticks (this browser); null: fit the panel again
  const onResizeHeight = useCallback((h) => {
    setManual(h || null);
    try { if (h) localStorage.setItem(HEIGHT_KEY, String(Math.round(h))); else localStorage.removeItem(HEIGHT_KEY); } catch { /* storage may be blocked */ }
  }, []);

  if (error) {
    return (
      <div className="wb-field-msg is-error" style={{ height: 120 }}>
        <span>The whiteboard could not be loaded: {error}</span>
        <button type="button" className="wb-text-btn" onClick={load}>Try again</button>
      </div>
    );
  }
  if (!data || !session) {
    return <div ref={hostRef} className="wb-field-msg wb-skeleton" style={{ height }}><span>Loading the whiteboard…</span></div>;
  }

  const board = (
    <Board model={modelRef.current} files={filesRef.current} session={session} canEdit={!!data.canEdit}
      full={full} onToggleFull={setFull} fieldId={fieldId} fieldName={fieldName || data.field?.name} workItem={data.workItem}
      height={height} onResizeHeight={onResizeHeight} memory={memory.current} onAttachmentAdded={onAttachmentAdded} />
  );
  return (
    <div ref={hostRef} className="wb-field">
      {full ? (
        <>
          <div className="wb-field-msg" style={{ height: 120 }}>
            <Icon name="expand" size={18} />
            <span>{fieldName || 'The whiteboard'} is open in full screen.</span>
            <button type="button" className="wb-text-btn" onClick={() => setFull(false)}>Back to the work item</button>
          </div>
          {createPortal(<div ref={fullRef} className="wb-full-host">{board}</div>, document.body)}
        </>
      ) : board}
      {data.updatedAt && !full && (
        <div className="wb-field-foot">Updated {when(data.updatedAt)}{data.updatedBy?.name ? ` by ${data.updatedBy.name}` : ''}</div>
      )}
    </div>
  );
}
